import { onMounted, onUnmounted } from 'vue';
import type { Ref } from 'vue';

/**
 * Approves the current review on "a" or Enter, opens its manage link (if any) on "m", skips it
 * (if skipping is currently allowed) on "s". Ignored while a modifier key is held.
 */
// Focus already on something with its own Enter/click semantics (the Back link, Manage, Approve
// itself, a future input): defer to that, or Enter there gets hijacked into approving instead of
// activating the focused element, since preventDefault() here cancels its native default action
// even though this listener runs after it in the event's lifecycle.
function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && Boolean(target.closest('a, button, input, textarea, select'));
}

export function useReviewHotkeys(
  onApprove: () => void,
  manageLink: Ref<string | undefined>,
  onSkip: () => void,
  canSkip: Ref<boolean>
): void {
  function handleKeydown(event: KeyboardEvent) {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (isInteractiveTarget(event.target)) return;

    if (event.key === 'a' || event.key === 'Enter') {
      event.preventDefault();
      onApprove();
      return;
    }

    if (event.key === 'm' && manageLink.value) {
      event.preventDefault();
      window.open(manageLink.value, '_blank', 'noreferrer');
      return;
    }

    if (event.key === 's' && canSkip.value) {
      event.preventDefault();
      onSkip();
    }
  }

  onMounted(() => window.addEventListener('keydown', handleKeydown));
  onUnmounted(() => window.removeEventListener('keydown', handleKeydown));
}
